import React, { useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useDayPlan } from "../../DayPlanProvider";
import { PlanItem } from "../../types/dayPlan.types";
import { InlineEditCell } from "./InlineEditCell";
import { DragHandle } from "./DragHandle";
import { DiscountCell } from "./DiscountCell";
import { useToast } from "../../../../contexts/ToastContext";
import { CATEGORY_OPTIONS } from "./CustomItemForm";

interface PlanItemRowProps {
  item: PlanItem;
}

export const PlanItemRow: React.FC<PlanItemRowProps> = ({
  item,
}) => {
  const { updatePlanItem, deletePlanItem } = useDayPlan();
  const { addToast } = useToast();
  const [isHovered, setIsHovered] = useState(false);
  const [isNotesExpanded, setIsNotesExpanded] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getCategoryIcon = (categoryType?: string, customOptionType?: string) => {
    // For custom items with a category assigned, use the category icon
    const effectiveType = categoryType === "custom" && customOptionType
      ? customOptionType
      : categoryType;

    switch (effectiveType) {
      case "accommodation":
        return "🛏️";
      case "gastronomy":
        return "🍽️";
      case "conference":
        return "🎤";
      case "additional":
        return "⭐";
      case "custom":
        return "✨";
      default:
        return "✨";
    }
  };

  const handleUpdate = async (field: string, value: any) => {
    try {
      await updatePlanItem(item.id, { [field]: value });
    } catch (error) {
      console.error("Failed to update item:", error);
      throw error;
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Czy na pewno chcesz usunąć tę pozycję?")) {
      try {
        await deletePlanItem(item.id);
      } catch (error) {
        console.error("Failed to delete item:", error);
        addToast({ type: "error", message: "Nie udało się usunąć pozycji" });
      }
    }
  };

  const calculateTotal = () => {
    const basePrice = item.quantity * item.unitPrice;

    if (!item.discountType || !item.discountValue) {
      return basePrice;
    }

    if (item.discountType === "percentage") {
      return basePrice * (1 - item.discountValue / 100);
    } else if (item.discountType === "amount") {
      // Rabat kwotowy jest za sztukę - mnożymy przez ilość
      const totalDiscount = item.discountValue * item.quantity;
      return Math.max(0, basePrice - totalDiscount);
    }

    return basePrice;
  };

  const showTimeInputs = true; // wszystkie elementy w day plan mogą mieć godziny (accommodation jest już odfiltrowane)

  // E2E test discriminator. Prefer the resolved category type from the linked
  // category option (e.g. "gastronomy", "conference"), then fall back to the
  // custom option type the user picked for an ad-hoc item, and finally to a
  // generic "custom" label. Spec AC-10 (2026-05-28-wybor-zrodlowej-wersji-oferty)
  // needs `line-item-{category}` to assert the gastronomy item survives a
  // duplicate-from-V1 flow when V2 dropped it.
  const lineItemCategory =
    item.categoryType === "custom"
      ? item.customOptionType ?? "custom"
      : item.categoryType ?? "custom";

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white border border-gray-200 hover:bg-gray-50 transition-colors ${
        isDragging ? "shadow-lg z-50" : ""
      }`}
      data-testid={`line-item-${lineItemCategory}`}
      data-line-item-id={item.id}
      data-line-item-category={lineItemCategory}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Main row */}
      <div
        style={{
          gridTemplateColumns: "32px 72px 72px 1fr 80px 100px 80px 100px 40px", // Stałe szerokości kolumn + kolumna akcji
        }}
        className="grid gap-2 py-2 px-4"
      >
        {/* Drag Handle - 32px */}
        <div className="flex items-center justify-center w-8">
          {isHovered && (
            <DragHandle listeners={listeners} attributes={attributes} />
          )}
        </div>

        {/* Time From - 72px */}
        <div className="w-18 flex items-center">
          {showTimeInputs && (
            <InlineEditCell
              value={item.timeFrom || ""}
              type="time"
              onSave={(value: string) => handleUpdate("timeFrom", value)}
              className="w-full text-xs text-center"
            />
          )}
        </div>

        {/* Time To - 72px */}
        <div className="w-18 flex items-center">
          {showTimeInputs && (
            <InlineEditCell
              value={item.timeTo || ""}
              type="time"
              onSave={(value: string) => handleUpdate("timeTo", value)}
              className="w-full text-xs text-center"
            />
          )}
        </div>

        {/* Element Title - flexible */}
        <div className="flex items-center space-x-2 min-w-0">
          <span className="text-lg flex-shrink-0">
            {getCategoryIcon(item.categoryType, item.customOptionType)}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center">
              {/* Inline category select for custom items (without categoryOptionId) */}
              {!item.categoryOptionId && (
                <select
                  value={item.customOptionType || ""}
                  onChange={async (e) => {
                    try {
                      await updatePlanItem(item.id, {
                        customOptionType: e.target.value || null,
                      });
                    } catch (error) {
                      console.error("Failed to update category:", error);
                      addToast({ type: "error", message: "Nie udało się zmienić kategorii" });
                    }
                  }}
                  className="mr-2 px-1 py-0.5 text-xs border border-gray-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 flex-shrink-0"
                  aria-label="Kategoria na podsumowaniu"
                >
                  {CATEGORY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              )}
              {!item.categoryOptionId ? (
                <InlineEditCell
                  value={item.title}
                  type="text"
                  onSave={(value: string) => handleUpdate("customTitle", value)}
                  className="flex-1 text-sm font-medium text-left"
                  placeholder="Nazwa pozycji..."
                />
              ) : (
                <div className="flex-1 text-sm font-medium text-gray-900 text-left">
                  {item.title}
                </div>
              )}
              {/* Chevron for notes */}
              <button
                onClick={() => setIsNotesExpanded(!isNotesExpanded)}
                className="ml-2 p-1 text-gray-400 hover:text-gray-600 text-xs"
                title={isNotesExpanded ? "Zwiń opis" : "Rozwiń opis"}
              >
                {isNotesExpanded ? "▼" : "▶"}
              </button>
            </div>
            {!isNotesExpanded && item.notes && (
              <div className="text-xs text-gray-500 mt-1 text-left truncate">
                {item.notes}
              </div>
            )}
          </div>
        </div>

        {/* Quantity - 80px */}
        <div className="w-20" data-testid="plan-item-quantity">
          <InlineEditCell
            value={item.quantity}
            type="number"
            onSave={(value: string) =>
              handleUpdate("quantity", parseInt(value) || 1)
            }
            className="w-full text-xs text-center"
            inputProps={{ min: 1 }}
          />
          <div className="text-xs text-gray-500 text-center mt-1">
            {item.unit}
          </div>
        </div>

        {/* Unit Price - 100px */}
        <div className="w-25 flex items-center">
          <InlineEditCell
            value={item.unitPrice}
            type="number"
            onSave={(value: string) =>
              handleUpdate("unitPrice", parseFloat(value) || 0)
            }
            className="w-full text-xs text-center"
            inputProps={{ min: 0, step: 0.01 }}
            suffix="zł"
          />
        </div>

        {/* Discount - 80px */}
        <div className="w-20 flex items-center">
          <DiscountCell
            discountType={item.discountType}
            discountValue={item.discountValue}
            quantity={item.quantity}
            unitPrice={item.unitPrice}
            onDiscountChange={async (updates) => {
              try {
                // updatePlanItem already refreshes offerDays internally
                await updatePlanItem(item.id, updates);
              } catch (error) {
                console.error("Failed to update discount:", error);
                throw error;
              }
            }}
            className="w-full"
          />
        </div>

        {/* Total - 100px */}
        <div className="w-25 flex items-center justify-end">
          <div className="text-sm font-medium text-gray-900 text-right">
            {Math.round(calculateTotal())} zł
          </div>
        </div>

        {/* Actions - 40px */}
        <div className="w-10 flex items-center justify-center">
          {isHovered && (
            <button
              onClick={handleDelete}
              className="text-red-500 hover:text-red-700 text-xs"
              title="Usuń pozycję"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Expanded notes section */}
      {isNotesExpanded && (
        <div className="px-4 pb-3 pt-1 border-t border-gray-100">
          <div className="text-xs font-medium text-gray-700 mb-2">Opis:</div>
          <InlineEditCell
            value={item.notes || ""}
            type="textarea"
            onSave={(value: string) => handleUpdate("notes", value)}
            className="w-full text-sm min-h-16"
            placeholder="Kliknij aby dodać opis..."
            textareaProps={{ rows: 3 }}
          />
        </div>
      )}
    </div>
  );
};
